import { whenIntercomReady } from "./intercom-sdk";

/**
 * Whether Intercom is flagging something that is not an unread reply.
 *
 * A ticket update is the case this exists for: it is every bit as much "there
 * is something to see" as a message, but Intercom does not count it in the
 * unread total and offers no callback for it. It marks the launcher with a
 * small dot instead, so the DOM is the only signal there is.
 *
 * That makes this the one place the integration watches Intercom's markup for
 * state rather than being told. The failure modes are mild in both directions
 * — a launcher that lingers, or one that misses a ticket update — and the
 * alternative is not surfacing ticket updates at all.
 */
const PASSIVE_DOT_SELECTOR = '[data-testid="passive-notifications"]';

const ATTACH_POLL_MS = 500;
const ATTACH_TIMEOUT_MS = 30_000;

let present = false;
let notifyChanged: (() => void) | undefined;

export const hasPassiveNotification = () => present;

function readFromDom(): boolean {
  const dot = document.querySelector(PASSIVE_DOT_SELECTOR);
  if (!(dot instanceof HTMLElement)) return false;
  // Presence is not the test. Intercom keeps its elements in the document and
  // collapses them instead of removing them — the same trap that made a closed
  // messenger read as open.
  const { width, height } = dot.getBoundingClientRect();
  return width > 0 && height > 0;
}

function refresh() {
  const next = readFromDom();
  if (next === present) return;
  present = next;
  notifyChanged?.();
}

/**
 * Watches for the dot appearing or clearing, and calls `onChange` when it
 * flips. Returns a canceller.
 *
 * Scoped to Intercom's own container: observing the whole document would mean
 * a callback on every React render in the app.
 */
export function watchPassiveNotifications(onChange: () => void) {
  notifyChanged = onChange;

  let observer: MutationObserver | undefined;
  let pollId: ReturnType<typeof setInterval> | undefined;

  const cancelReady = whenIntercomReady(() => {
    // The container is rendered a moment after the SDK arrives, so it cannot
    // simply be looked up here.
    const startedAt = Date.now();
    pollId = setInterval(() => {
      const container = document.querySelector("#intercom-container");
      if (container) {
        clearInterval(pollId);
        observer = new MutationObserver(refresh);
        // Attributes as well as children: Intercom may clear the dot by
        // restyling it in place rather than removing it, and a childList-only
        // watch would sleep through that.
        observer.observe(container, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["class", "style"],
        });
        refresh();
        return;
      }
      if (Date.now() - startedAt > ATTACH_TIMEOUT_MS) clearInterval(pollId);
    }, ATTACH_POLL_MS);
  });

  return () => {
    cancelReady();
    if (pollId) clearInterval(pollId);
    observer?.disconnect();
    notifyChanged = undefined;
  };
}

/** Belongs to the session being torn down; the next one reports its own. */
export function resetPassiveNotifications() {
  present = false;
}
